import { api } from 'api';
import { FUNC_DEFAULT } from 'constants';
import {
  FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME,
  FORM_ADD_PAYTO_BANK_NAME_NAME,
  FORM_ADD_PAYTO_BSB_NAME,
  FORM_ADD_PAYTO_MAX_AMOUNT_NAME,
  FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME,
  FORM_ADD_PAYTO_PAYID_TYPE_NAME,
  PAYID_TYPE_EMAIL,
  PAYID_TYPE_MOBILE,
  PAYTO_LOG_TAG,
} from 'features/to/const';
import { checkMaintenance } from 'helpers/maintenance';
import useAuth from 'hooks/useAuth';
import useCommonStore from 'hooks/useCommonStore';
import { isEmpty } from 'lodash';

const usePaytoAPI = () => {
  const { token } = useAuth();
  const { setLoading } = useCommonStore();

  const payloadTransformFromPaytoAccountInformation = (
    paytoAccountInformation
  ) => {
    if (isEmpty(paytoAccountInformation)) return {};
    return {
      accountNumber:
        paytoAccountInformation?.[FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME],
      bankName: paytoAccountInformation?.[FORM_ADD_PAYTO_BANK_NAME_NAME],
      bsb: paytoAccountInformation?.[FORM_ADD_PAYTO_BSB_NAME],
      maxAmount: Number(
        paytoAccountInformation?.[FORM_ADD_PAYTO_MAX_AMOUNT_NAME]
      ),
      senderBankId: paytoAccountInformation?.id,
      paymentAgreementUID: paytoAccountInformation?.id,
      bankCode: paytoAccountInformation?.bankCode,
      payId: paytoAccountInformation?.[FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME],
    };
  };

  const payloadTransformFromPaytoAccountAgreement = (accountAgreement) => {
    if (isEmpty(accountAgreement)) return {};
    const {
      paymentAgreementUID,
      bsb,
      bankName,
      bankCode,
      maxAmount,
      accountNumber,
      payId,
      amendPayToLimit,
    } = accountAgreement || {};
    return {
      [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]: maxAmount,
      [FORM_ADD_PAYTO_BSB_NAME]: bsb,
      [FORM_ADD_PAYTO_BANK_NAME_NAME]: bankName || '',
      [FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME]: accountNumber,
      [FORM_ADD_PAYTO_PAYID_TYPE_NAME]: payId
        ? payId.slice(0, 2) === '04'
          ? PAYID_TYPE_MOBILE
          : PAYID_TYPE_EMAIL
        : '',
      [FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME]: payId,
      id: paymentAgreementUID,
      bankCode,
      // DEV-1401: authoritative per-agreement amend limit from
      // getPaytoAccountsAgreement, keyed to THIS agreement's own
      // paymentAgreementUID -- read directly by EditPaytoAccountAgreementModal
      // rather than folded into any create-flow limit state, so a create
      // limit and an amend limit are never the same store value. Distinct
      // from the Create flow's own paytoAmendLimit store slot (see
      // usePaytoStore.jsx), which is sourced from a live BSB/PayID
      // validation response rather than from this per-agreement transform.
      amendPayToLimit,
    };
  };

  const fetchPaytoAccountsAgreement = async (callback = FUNC_DEFAULT) => {
    try {
      setLoading(true);
      const response = await api.getPaytoAccountsAgreement(token);
      let { data } = response || {};

      /* TEST CASES */
      /* 1. Add 1st account successfully - BEGIN */
      // data = [];
      // data = [data[0]];
      /* ----------END---------- */
      /* 2. Account Agreement is blocked- BEGIN */
      // data = [
      //   ...data,
      //   {
      //     ...data[0],
      //     blocked: true,
      //   },
      // ];
      /* ----------END---------- */
      /* 3. Account Agreement is pendingAmend - BEGIN */
      // data = [
      //   {
      //     ...data[0],
      //     pendingAmend: true,
      //   },
      //   {
      //     ...data[1],
      //     pendingAmend: false,
      //   },
      // ];
      /* ----------END---------- */
      /* 4. Account Agreement is status=created - BEGIN */
      // data = [
      //   {
      //     ...data[0],
      //     paymentAgreementStatus: 'Created',
      //     // blocked: true,
      //     // maxAmount: 20,
      //   },
      //   {
      //     ...data[1],
      //     paymentAgreementStatus: 'Created',
      //     // maxAmount: 20,
      //   },
      // ];
      /* ----------END---------- */
      /* 4. Account Agreement is status=active - BEGIN */
      // data = [
      //   {
      //     ...data[0],
      //     paymentAgreementStatus: 'Active',
      //     // blocked: true,
      //     maxAmount: 6000,
      //   },
      //   ...(data[1]
      //     ? [
      //         {
      //           ...data[1],
      //           paymentAgreementStatus: 'Active',
      //           // maxAmount: 20,
      //         },
      //       ]
      //     : []),
      // ];
      // data = [
      //   data[0],
      //   {
      //     ...data[1],
      //     pendingAmend: false,
      //     maxAmount: 3500,
      //   },
      // ];
      /* ----------END---------- */

      console.log(
        `⭐ ${PAYTO_LOG_TAG} 👉 GET ➡️ fetchPaytoAccountsAgreement ➡️ data ↩`,
        data
      );
      callback(data || []);
      setLoading(false);
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 ${PAYTO_LOG_TAG} ❌ GET ➡️ fetchPaytoAccountsAgreement ↩`,
        error?.message
      );
      setLoading(false);
    }
  };

  const fetchPaytoBankAccountsVerified = async (callback = FUNC_DEFAULT) => {
    try {
      setLoading(true);
      const response = await api.getPaytoBankAccountsVerified(token);
      const { data } = response || {};
      console.log(
        `⭐ ${PAYTO_LOG_TAG} 👉 GET ➡️ fetchPaytoBankAccountsVerified ➡️ data ↩`,
        data
      );
      callback(data || []);
      setLoading(false);
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 ${PAYTO_LOG_TAG} ❌ GET ➡️ fetchPaytoBankAccountsVerified ↩`,
        error?.message
      );
      setLoading(false);
    }
  };

  const addPaytoAccountAgreement = async (
    paytoAccountInformation = null,
    callback = FUNC_DEFAULT,
    errorCallback = FUNC_DEFAULT
  ) => {
    try {
      const { senderBankId, maxAmount, accountNumber, bsb, bankCode, payId } =
        payloadTransformFromPaytoAccountInformation(paytoAccountInformation);
      const payload =
        senderBankId === 'other'
          ? {
              accountNumber,
              bsb,
              maxAmount,
              bankCode,
            }
          : senderBankId === 'payid'
          ? {
              payId,
              maxAmount,
            }
          : {
              senderBankId,
              maxAmount,
            };
      console.log(
        `⭐ ${PAYTO_LOG_TAG} 👉 POST ➡️ addPaytoAccountAgreement ➡️ payload ↩`,
        payload
      );
      const response = await api.addPaytoAccountAgreement(token, payload);
      const { data } = response || {};
      console.log(
        `⭐ ${PAYTO_LOG_TAG} 👉 POST ➡️ addPaytoAccountAgreement ➡️ data ↩`,
        data
      );
      callback(data || null);
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 ${PAYTO_LOG_TAG} ❌ POST ➡️ addPaytoAccountAgreement ↩`,
        error?.message
      );
      errorCallback?.(error?.response?.data);
      setLoading(false);
    }
  };

  const updatePaytoAccountAgreementById = async (
    accountAgreement = null,
    callback = FUNC_DEFAULT,
    errorCallback = FUNC_DEFAULT
  ) => {
    try {
      console.log(
        `⭐ ${PAYTO_LOG_TAG} 👉 PUT ➡️ updatePaytoAccountAgreementById ➡️ accountAgreement ↩`,
        accountAgreement
      );
      const response = await api.updatePaytoAccountAgreementById(
        token,
        accountAgreement
      );
      const { data } = response || {};
      console.log(
        `⭐ ${PAYTO_LOG_TAG} 👉 PUT ➡️ updatePaytoAccountAgreementById ➡️ data ↩`,
        data
      );
      callback(data || null);
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 ${PAYTO_LOG_TAG} ❌ PUT ➡️ updatePaytoAccountAgreementById ↩`,
        error?.message
      );
      errorCallback?.(error?.response?.data);
      setLoading(false);
    }
  };

  const deletePaytoAccountAgreementById = async (
    accountAgreement = null,
    callback = FUNC_DEFAULT,
    errorCallback = FUNC_DEFAULT
  ) => {
    try {
      const { paymentAgreementUID } = accountAgreement || {};
      console.log(
        `⭐ ${PAYTO_LOG_TAG} 👉 DELETE ➡️ deletePaytoAccountAgreementById ➡️ accountAgreement ↩`,
        accountAgreement
      );
      const response = await api.deletePaytoAccountAgreementById(
        token,
        paymentAgreementUID
      );
      const { data } = response || {};
      console.log(
        `⭐ ${PAYTO_LOG_TAG} 👉 DELETE ➡️ deletePaytoAccountAgreementById ➡️ data ↩`,
        data
      );
      callback(data || null);
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 ${PAYTO_LOG_TAG} ❌ DELETE ➡️ deletePaytoAccountAgreementById ↩`,
        error?.message
      );
      errorCallback?.(error?.response?.data);
      setLoading(false);
    }
  };

  const checkStatusPaytoAccountAgreementById = async (
    accountAgreement = null,
    callback = FUNC_DEFAULT,
    errorCallback = FUNC_DEFAULT
  ) => {
    try {
      const { paymentAgreementUID } = accountAgreement || {};
      console.log(
        `⭐ ${PAYTO_LOG_TAG} 👉 GET ➡️ checkStatusPaytoAccountAgreementById ➡️ accountAgreement ↩`,
        accountAgreement
      );
      const response = await api.checkStatusPaytoAccountAgreementById(
        token,
        paymentAgreementUID
      );
      let { data } = response || {};
      /* TEST CASES */
      /* 1. Status is Created */
      // data = {
      //   ...data,
      //   paymentAgreementStatus: 'Created',
      //   pendingAmend: false
      // };
      /* 2. Status is Active */
      // data = {
      //   ...data,
      //   paymentAgreementStatus: 'Active',
      //   pendingAmend: false,
      // };
      /* 3. Status is Rejected */
      // data = {
      //   ...data,
      //   paymentAgreementStatus: 'Rejected' || 'Cancelled',
      //   pendingAmend: false,
      // };
      /* 4. Edit reject */
      // data = {
      //   ...data,
      //   pendingAmend: false,
      //   amendmentStatus: 'DECLINED',
      // };
      /* ----------END---------- */
      console.log(
        `⭐ ${PAYTO_LOG_TAG} 👉 GET ➡️ checkStatusPaytoAccountAgreementById ➡️ data ↩`,
        data
      );
      callback(data || []);
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 ${PAYTO_LOG_TAG} ❌ GET ➡️ checkStatusPaytoAccountAgreementById ↩`,
        error?.message
      );
      setLoading(false);
      errorCallback?.(error?.response?.data);
    }
  };

  const checkReceiverVerifiedByOTP = async (
    receiverId = '',
    callback = FUNC_DEFAULT,
    errorCallback = FUNC_DEFAULT,
    isResendViaEmail = false
  ) => {
    try {
      console.log(
        `⭐ ${PAYTO_LOG_TAG} 👉 GET ➡️ checkReceiverVerifiedByOTP ➡️ receiverId ↩`,
        receiverId
      );
      const response = await api.checkReceiverVerifiedByOTP(
        token,
        receiverId,
        isResendViaEmail
      );
      let { data } = response || {};
      console.log(
        `⭐ ${PAYTO_LOG_TAG} 👉 GET ➡️ checkReceiverVerifiedByOTP ➡️ data ↩`,
        data
      );
      if (!data?.isVerified) {
        callback(data || []);
        return false;
      }
      return data?.isVerified;
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 ${PAYTO_LOG_TAG} ❌ GET ➡️ checkReceiverVerifiedByOTP ↩`,
        error?.message
      );
      setLoading(false);
      errorCallback?.(error?.response?.data);
    }
  };

  return {
    fetchPaytoAccountsAgreement,
    fetchPaytoBankAccountsVerified,
    addPaytoAccountAgreement,
    checkStatusPaytoAccountAgreementById,
    deletePaytoAccountAgreementById,
    updatePaytoAccountAgreementById,
    payloadTransformFromPaytoAccountInformation,
    payloadTransformFromPaytoAccountAgreement,
    checkReceiverVerifiedByOTP,
  };
};

export default usePaytoAPI;
